import { inject } from 'mobx-react';
import useLanguageChange from '../../../utils/hooks/useLanguageChange';
import { useState, useEffect, useMemo } from 'react';
import Utils, { getSelectStyles } from '../../../utils/utils';
import Select from 'react-select';
import { getExpenseFormLabel } from '../../../utils/expense-form-labels';

const isBlank = (value) => value == null || String(value).trim() === '';
const RequiredMark = () => <span className='text-danger'>*</span>;
const normalizeMoneyValue = (value) => {
  const numericValue = Number.parseFloat(String(value).replace(',', '.'));
  if (Number.isNaN(numericValue)) {
    return '';
  }
  return Math.max(0, numericValue).toFixed(2);
};

const ExpenseInfo = ({ data, expenseInfo, setExpenseInfo, additionalComponent, onValidationChange, menuPortalTarget = document.body, hideNarrative = false, compactOptionalFields = false }) => {
  const { supplier_event, narrative, amount, taxCode, tip, conversionTrace } = expenseInfo;
  const showSupplierEvent = Utils.hasExpenseField(data, 'field_supplier_event');
  const isSupplierEventRequired = Utils.isExpenseFieldRequired(data, 'field_supplier_event');
  const isNarrativeRequired = Utils.isExpenseFieldRequired(data, 'field_narrative');
  const isTipRequired = Utils.isExpenseFieldRequired(data, 'field_tips');
  const isTaxCodeRequired = Utils.isExpenseFieldRequired(data, 'field_tax_code');
  const [errors, setErrors] = useState({
    supplier_event: isSupplierEventRequired && isBlank(supplier_event),
    narrative: isNarrativeRequired && isBlank(narrative),
    amount: !amount,
    tip: isTipRequired && isBlank(tip),
    taxCode: isTaxCodeRequired && isBlank(taxCode),
    location: false,
  });
  const [touched, setTouched] = useState({
    supplier_event: false,
    narrative: false,
    amount: false,
    tip: false,
    taxCode: false,
    location: false,
  });

  useLanguageChange(data.t);

  const taxCodeOptions = useMemo(() => {
    return (data.config?.tax_profile || []).map(tax => ({
      label: tax.code,
      value: tax.code
    }));
  }, [data.config?.tax_profile]);

  const taxCodeValue = useMemo(() => {
    if (!taxCode) return null;
    return taxCodeOptions.find(option => option.value === taxCode) || null;
  }, [taxCode, taxCodeOptions]);
  const displayAmount = touched.amount
    ? (amount || '')
    : (isBlank(amount) ? '' : normalizeMoneyValue(amount));
  const displayTip = touched.tip
    ? (tip || '')
    : (isBlank(tip) ? '' : normalizeMoneyValue(tip));

  useEffect(() => {
    const newErrors = {
      supplier_event: isSupplierEventRequired && isBlank(supplier_event),
      narrative: isNarrativeRequired && isBlank(narrative),
      amount: !amount,
      tip: isTipRequired && isBlank(tip),
      taxCode: isTaxCodeRequired && isBlank(taxCode),
    };

    const hasErrorsChanged = Object.keys(newErrors).some((key) => newErrors[key] !== errors[key]);

    if (hasErrorsChanged) {
      setErrors(newErrors);
    }
  }, [amount, supplier_event, narrative, tip, taxCode, isSupplierEventRequired, isNarrativeRequired, isTipRequired, isTaxCodeRequired]);

  useEffect(() => {
    if (onValidationChange) {
      const isValid = !Object.values(errors).some((error) => error);
      onValidationChange('info', isValid);
    }
  }, [errors]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    if (['amount', 'supplier_event', 'narrative', 'tip'].includes(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: name === 'amount' ? !value : {
          supplier_event: isSupplierEventRequired,
          narrative: isNarrativeRequired,
          tip: isTipRequired,
        }[name] && isBlank(value),
      }));
    }
    if (name === 'location') {
      // Location validation is handled in parent component
      setErrors((prev) => ({
        ...prev,
        location: false, // Will be set by parent validation
      }));
    }

    setExpenseInfo({
      ...expenseInfo,
      [name]: value,
    });
  };

  const handleAmountChange = (e) => {
    setTouched((prev) => ({
      ...prev,
      amount: true,
    }));

    const normalizedValue = normalizeMoneyValue(e.target.value);
    if (!isBlank(normalizedValue)) {
      setExpenseInfo({
        ...expenseInfo,
        amount: normalizedValue,
      });

      setErrors((prev) => ({
        ...prev,
        amount: false,
      }));
    } else {
      setExpenseInfo({
        ...expenseInfo,
        amount: '',
      });

      setErrors((prev) => ({
        ...prev,
        amount: true,
      }));
    }
  };

  const handleMoneyFieldChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = normalizeMoneyValue(value);
    
    setExpenseInfo({
      ...expenseInfo,
      [name]: normalizedValue,
    });
  };

  // The invoice modal places optional review fields in one disclosure. Required
  // fields stay in the main form; the regular review screen keeps its layout.
  const supplierEventField = (
    <div className='d-flex pb-2'>
      <span className='report-label col-2'>
        {getExpenseFormLabel(data, 'field_supplier_event')} {isSupplierEventRequired && <RequiredMark />}
      </span>
      <div className='col-8'>
        <input
          className={`form-control ${errors.supplier_event && touched.supplier_event ? 'is-invalid' : ''}`}
          placeholder={data.t.read('review_expense.supplier_event.placeholder')}
          type='text'
          name='supplier_event'
          data-testid='supplier_event-input'
          required={isSupplierEventRequired}
          onChange={(e) => handleInputChange(e)}
          onBlur={() => setTouched({ ...touched, supplier_event: true })}
          value={supplier_event || ''}
        />
        {touched.supplier_event && errors.supplier_event && <div data-testid="supplier_event-error" className='text-danger mt-1 pl-1'>{data.t.read('form_errors.required_field')}</div>}
      </div>
    </div>
  );

  const tipField = (
    <div className='d-flex pb-2'>
      <span className='report-label col-2'>{getExpenseFormLabel(data, 'field_tips')} {isTipRequired && <RequiredMark />}</span>
      <div className='col-8'>
        <input
          className={`form-control ${errors.tip && touched.tip ? 'is-invalid' : ''}`}
          placeholder={data.t.read('tips')}
          value={displayTip}
          type='text'
          inputMode='decimal'
          name='tip'
          data-testid='tip-input'
          required={isTipRequired}
          onChange={(e) => handleInputChange(e)}
          onBlur={(e) => {
            handleMoneyFieldChange(e);
            setTouched({ ...touched, tip: true });
          }}
        />
        {touched.tip && errors.tip && <div data-testid="tip-error" className='text-danger mt-1 pl-1'>{data.t.read('form_errors.required_field')}</div>}
      </div>
    </div>
  );

  const taxCodeField = (
    <div className='d-flex pb-2'>
      <span className='report-label col-2'>{getExpenseFormLabel(data, 'field_tax_code')} {isTaxCodeRequired && <RequiredMark />}</span>
      <div className='col-8'>
        <Select
          classNamePrefix='select'
          name='taxCode'
          data-testid='taxCode-input'
          value={taxCodeValue}
          options={taxCodeOptions}
          placeholder={data.t.read("tax_codes")}
          onChange={(choice) => {
            setTouched({ ...touched, taxCode: true });
            setErrors((prev) => ({
              ...prev,
              taxCode: isTaxCodeRequired && !choice?.value,
            }));
            setExpenseInfo({
              ...expenseInfo,
              taxCode: choice?.value || null,
            });
          }}
          onBlur={() => {
            setTouched({ ...touched, taxCode: true });
          }}
          styles={getSelectStyles(errors.taxCode, touched.taxCode)}
          menuPortalTarget={menuPortalTarget}
          menuPosition='fixed'
        />
        {touched.taxCode && errors.taxCode && <div data-testid="taxCode-error" className='text-danger mt-1 pl-1'>{data.t.read('form_errors.required_field')}</div>}
      </div>
    </div>
  );

  return (
    <div>
      {showSupplierEvent && (!compactOptionalFields || isSupplierEventRequired) && supplierEventField}
      {additionalComponent}
      {!hideNarrative && Utils.hasExpenseField(data, 'field_narrative') && (
        <div className='d-flex pb-2'>
          <span className='report-label col-2'>
            {getExpenseFormLabel(data, 'field_narrative')} {isNarrativeRequired && <RequiredMark />}
          </span>
          <div className='col-8'>
            <textarea
              value={narrative}
              className={`form-control ${errors.narrative && touched.narrative ? 'is-invalid' : ''}`}
              placeholder={data.t.read('review_expense.narrative.placeholder')}
              name='narrative'
              data-testid='narrative-input'
              required={isNarrativeRequired}
              onChange={(e) => handleInputChange(e)}
              onBlur={() => setTouched({ ...touched, narrative: true })}
              rows='4'
            />
            {touched.narrative && errors.narrative && <div data-testid="narrative-error" className='text-danger mt-1 pl-1'>{data.t.read('form_errors.required_field')}</div>}
          </div>
        </div>
      )}

      {(!compactOptionalFields || isTipRequired) && Utils.hasExpenseField(data, 'field_tips') && tipField}
      <div className='d-flex pb-2'>
        <span className='report-label col-2'>{getExpenseFormLabel(data, 'field_amount')} <RequiredMark /></span>
        <div className='col-8'>
          <input
            className={`form-control ${errors.amount && touched.amount ? 'is-invalid' : ''}`}
            placeholder={data.t.read('review_expense.amount.placeholder')}
            value={displayAmount}
            required
            type='text'
            inputMode='decimal'
            name='amount'
            data-testid='amount-input'
            onChange={(e) => handleInputChange(e)}
            onBlur={(e) => {
              handleAmountChange(e);
              setTouched({ ...touched, amount: true });
            }}
          />
          {touched.amount && errors.amount && <div data-testid="amount-error" className='text-danger mt-1 pl-1'>{data.t.read('form_errors.required_field')}</div>}
          {conversionTrace && (
            <div data-testid='conversion-trace' className='text-muted small font-italic mt-1 pl-1'>
              {data.t.read('review_expense.converted_from')
                .replace('{amount}', Number(conversionTrace.originalAmount ?? 0).toFixed(2))
                .replace('{currency}', conversionTrace.originalCurrency)
                .replace('{rate}', conversionTrace.rate)
                .replace('{date}', conversionTrace.rateDate || '')
                .replace('{source}', (conversionTrace.rateSource || '').toLowerCase() === 'boc' ? 'BoC' : (conversionTrace.rateSource || 'BoC'))}
            </div>
          )}
        </div>
      </div>

      {(!compactOptionalFields || isTaxCodeRequired) && Utils.hasExpenseField(data, 'field_tax_code') && taxCodeField}
      {compactOptionalFields && (
        (showSupplierEvent && !isSupplierEventRequired) ||
        (Utils.hasExpenseField(data, 'field_tips') && !isTipRequired) ||
        (Utils.hasExpenseField(data, 'field_tax_code') && !isTaxCodeRequired)
      ) && (
        <details className='expense-more-details'>
          <summary>{data.t.read('invoice.add_expense.more_details')}</summary>
          {showSupplierEvent && !isSupplierEventRequired && supplierEventField}
          {Utils.hasExpenseField(data, 'field_tips') && !isTipRequired && tipField}
          {Utils.hasExpenseField(data, 'field_tax_code') && !isTaxCodeRequired && taxCodeField}
        </details>
      )}
    </div>
  );
};

export default inject('data')(ExpenseInfo);
